import React, { useMemo, useState } from 'react';
import Chart from 'react-apexcharts';
import MainContent from '../../components/MainContent';
import PageContainer from '../../components/PageContainer';
import PageTitle from '../../components/Common/PageTitle/PageTitle';

// ===================== Dummy Data (replace with API) =====================
const ASSETS = [
  { id: 'a1', name: '1000 Cash on hand', balance: 922050000 },
  { id: 'a2', name: '1010 Checking Account', balance: 153300000 },
  { id: 'a3', name: 'Accounts Receivable', balance: 8200000 },
  { id: 'a4', name: 'Inventory', balance: 4500000 },
];

const LIABS = [
  { id: 'l1', name: 'Accounts Payable', balance: 6000000, due: 2200000 },
  { id: 'l2', name: '2001 Credit Card', balance: 500000 },
  { id: 'l3', name: 'Loan Payable', balance: 10000000, due: 10000000 },
  { id: 'l4', name: 'Accrued Expenses', balance: 1800000, due: 900000 },
];

const EXPENSES = [
  { id: 'e1', name: '6010 Online Marketing', amount: 10000000 },
  { id: 'e2', name: '6020 Subscriptions', amount: 6000000 },
  { id: 'e3', name: '6090 Depreciation', amount: 2000000 },
];

const INCOME_LAST_30 = 500000; // for P&L tile demo
const EXPENSE_LAST_30 = 18000000;

const INVOICES = {
  unpaidTotal: 147344000,
  overdue: 147344000,
  notDue: 0,
  paidLast30: 0,
  deposited: 0,
  notDeposited: 0,
};

// Recent ledger activity (dummy)
const LEDGER_JOURNALS = [
  {
    id: 'j1',
    date: '2025-09-18',
    ref: 'PAY-0105',
    memo: 'Supplier payment — Jinja Distributors',
    lines: [
      { accountCode: '2010', accountName: 'Accounts Payable', dr: 0, cr: 450000 },
      { accountCode: '1010', accountName: 'Checking', dr: 450000, cr: 0 },
    ],
  },
  {
    id: 'j2',
    date: '2025-09-17',
    ref: 'INV-0456',
    memo: 'Invoice to Elite High School',
    lines: [
      { accountCode: '1100', accountName: 'Accounts Receivable', dr: 1250000, cr: 0 },
      { accountCode: '4000', accountName: 'Sales Revenue', dr: 0, cr: 1250000 },
    ],
  },
  {
    id: 'j3',
    date: '2025-09-16',
    ref: 'DEP-0021',
    memo: 'Bank deposit',
    lines: [
      { accountCode: '1010', accountName: 'Checking', dr: 950000, cr: 0 },
      { accountCode: '1100', accountName: 'Accounts Receivable', dr: 0, cr: 950000 },
    ],
  },
  {
    id: 'j4',
    date: '2025-09-15',
    ref: 'BILL-0312',
    memo: 'Facebook ads',
    lines: [
      { accountCode: '6010', accountName: 'Marketing Expense', dr: 600000, cr: 0 },
      { accountCode: '2001', accountName: 'Credit Card Payable', dr: 0, cr: 600000 },
    ],
  },
  {
    id: 'j5',
    date: '2025-09-15',
    ref: 'PAY-0104',
    memo: 'Card payment',
    lines: [
      { accountCode: '2001', accountName: 'Credit Card Payable', dr: 300000, cr: 0 },
      { accountCode: '1010', accountName: 'Checking', dr: 0, cr: 300000 },
    ],
  },
  {
    id: 'j6',
    date: '2025-09-14',
    ref: 'JRN-0009',
    memo: 'Monthly depreciation',
    lines: [
      { accountCode: '6090', accountName: 'Depreciation Expense', dr: 200000, cr: 0 },
      { accountCode: '1600', accountName: 'Accumulated Depreciation', dr: 0, cr: 200000 },
    ],
  },
];

// Trends (Sales sparkline)
const salesTrend = [0, 0, 0, 0, 0, 0];

function toUGX(n) {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(Number(n));
  return `${sign}UGX ${abs.toLocaleString('en-UG', { maximumFractionDigits: 0 })}`;
}

// ===================== Small helpers =====================
const CardHeader = ({ title, right }) => (
  <div className="d-flex justify-content-between align-items-center mb-2">
    <div className="text-uppercase small text-muted">{title}</div>
    {right && <div className="small text-muted">{right}</div>}
  </div>
);

// ===================== Cards =====================
const ProfitAndLossCard = ({ income, expense }) => {
  const net = income - expense;
  const denom = Math.max(income + expense, 1);
  const incPct = Math.min(100, Math.round((income / denom) * 100));
  const expPct = Math.min(100, Math.round((expense / denom) * 100));
  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Profit and Loss" right={<span>Last 30 days <i className="fa fa-chevron-down ml-1"/></span>} />
        <div className={`h4 ${net < 0 ? 'text-danger' : ''}`}>{toUGX(net)}</div>
        <div className="text-muted small mb-3">Net income for last 30 days</div>
        <span className="badge badge-light border small mb-3"><i className="fa fa-info-circle mr-1"/>100% categorized</span>
        <div className="mb-2 d-flex align-items-center">
          <div className="mr-3" style={{ width: 80 }}>
            <div className="small">{toUGX(income)}</div>
            <div className="text-muted small">Income</div>
          </div>
          <div className="flex-grow-1">
            <div className="progress" style={{ height: 12 }}>
              <div className="progress-bar bg-success" role="progressbar" style={{ width: `${incPct}%` }} aria-valuenow={income} />
            </div>
          </div>
        </div>
        <div className="mb-1 d-flex align-items-center">
          <div className="mr-3" style={{ width: 80 }}>
            <div className="small">{toUGX(expense)}</div>
            <div className="text-muted small">Expenses</div>
          </div>
          <div className="flex-grow-1">
            <div className="progress" style={{ height: 12 }}>
              <div className="progress-bar" role="progressbar" style={{ width: `${expPct}%`, backgroundColor: '#17a2b8' }} aria-valuenow={expense} />
            </div>
          </div>
        </div>
        <a href="#" className="small d-inline-block mt-3">See profit and loss report</a>
      </div>
    </div>
  );
};

const ExpensesDonutCard = ({ data }) => {
  const series = data.map(d => d.amount);
  const labels = data.map(d => d.name);
  const options = {
    chart: { type: 'donut' },
    labels,
    legend: { show: false },
    dataLabels: { enabled: false },
    stroke: { width: 0 },
    colors: ['#00897B', '#26A69A', '#004D40', '#80CBC4'],
    tooltip: { y: { formatter: (v) => toUGX(v) } },
  };
  const total = series.reduce((a,b)=>a+b,0);
  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Expenses" right={<span>Last 30 days <i className="fa fa-chevron-down ml-1"/></span>} />
        <div className="h4 mb-1">{toUGX(total)}</div>
        <div className="text-muted small mb-2">Total expenses</div>
        <div className="row no-gutters align-items-center">
          <div className="col-6 pr-3">
            {data.slice(0,3).map((d, idx) => (
              <div key={d.id} className="d-flex align-items-center mb-2">
                <span className="mr-2" style={{ width: 8, height: 8, borderRadius: '50%', background: options.colors[idx] }} />
                <div className="small">
                  <div className="font-weight-medium">{toUGX(d.amount)}</div>
                  <div className="text-muted" style={{maxWidth:180, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{d.name}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="col-6">
            <Chart options={options} series={series} type="donut" height={170} />
          </div>
        </div>
      </div>
    </div>
  );
};

const FinancialPositionCard = ({ assetsTotal, liabsTotal }) => {
  const netWorth = assetsTotal - liabsTotal;
  const denom = Math.max(assetsTotal + liabsTotal, 1);
  const aPct = Math.min(100, Math.round((assetsTotal / denom) * 100));
  const lPct = Math.min(100, Math.round((liabsTotal / denom) * 100));
  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Financial Position" right={<span>As of today</span>} />
        <div className="h4 mb-0">{toUGX(netWorth)}</div>
        <div className="text-muted small mb-3">Business net worth</div>
        <div className="mb-2 d-flex align-items-center">
          <div className="mr-3" style={{ width: 80 }}>
            <div className="small">{toUGX(assetsTotal)}</div>
            <div className="text-muted small">Assets</div>
          </div>
          <div className="flex-grow-1">
            <div className="progress" style={{ height: 12 }}>
              <div className="progress-bar bg-success" style={{ width: `${aPct}%` }} />
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className="mr-3" style={{ width: 80 }}>
            <div className="small">{toUGX(liabsTotal)}</div>
            <div className="text-muted small">Liabilities</div>
          </div>
          <div className="flex-grow-1">
            <div className="progress" style={{ height: 12 }}>
              <div className="progress-bar bg-info" style={{ width: `${lPct}%` }} />
            </div>
          </div>
        </div>
        <a href="#" className="small d-inline-block mt-3">See balance sheet</a>
      </div>
    </div>
  );
};

const BankAccountsCard = ({ accounts }) => (
  <div className="card mb-3 h-100">
    <div className="card-body">
      <CardHeader title="Bank Accounts" right={<i className="fa fa-pen"/>} />
      <ul className="list-group list-group-flush">
        {accounts.map(acc => (
          <li key={acc.id} className="list-group-item d-flex justify-content-between align-items-center px-0">
            <div>
              <div className="font-weight-medium">{acc.name} {acc.reviewed && <span className="badge badge-success ml-1">Reviewed</span>}</div>
              <div className="text-muted small">In YEXP</div>
            </div>
            <div className={`text-right ${acc.balance < 0 ? 'text-danger' : ''}`}>{toUGX(acc.balance)}</div>
          </li>
        ))}
      </ul>
      <div className="d-flex justify-content-between mt-2">
        <a href="#" className="small">Connect accounts</a>
        <a href="#" className="small">Go to registers <i className="fa fa-chevron-down ml-1"/></a>
      </div>
    </div>
  </div>
);

const InvoicesCard = ({ invoices }) => {
  const total = invoices.unpaidTotal;
  const overdue = invoices.overdue;
  const notDue = invoices.notDue;
  const pctOverdue = total ? Math.round((overdue/total)*100) : 0;
  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Invoices" right={<span>Last 365 days</span>} />
        <div className="d-flex justify-content-between align-items-baseline mb-3">
          <div>
            <div className="h4 mb-0">{toUGX(total)} <span className="text-muted small">Unpaid</span></div>
          </div>
          <div className="small text-muted">{toUGX(invoices.paidLast30)} Paid &nbsp;Last 30 days</div>
        </div>
        <div className="d-flex justify-content-between">
          <div>
            <div className="h5 mb-0">{toUGX(overdue)}</div>
            <div className="text-muted small">Overdue</div>
          </div>
          <div className="text-right">
            <div className="h5 mb-0">{toUGX(notDue)}</div>
            <div className="text-muted small">Not due yet</div>
          </div>
        </div>
        <div className="progress my-2" style={{ height: 10 }}>
          <div className="progress-bar bg-warning" style={{ width: `${pctOverdue}%` }} />
        </div>
        <div className="d-flex justify-content-between mt-2 text-muted small">
          <div>{toUGX(invoices.notDeposited)} Not deposited</div>
          <div>{toUGX(invoices.deposited)} Deposited</div>
        </div>
      </div>
    </div>
  );
};

const SalesCard = ({ series }) => {
  const options = {
    chart: { type: 'line', sparkline: { enabled: true } },
    stroke: { width: 3, curve: 'smooth' },
    markers: { size: 0 },
    colors: ['#28a745'],
    tooltip: { x: { show: false }},
  };
  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Sales" right={<span>Last 30 days <i className="fa fa-chevron-down ml-1"/></span>} />
        <div className="h4 mb-1">{toUGX(0)}</div>
        <div className="text-muted small mb-3">Total sales</div>
        <Chart options={options} series={[{ name: 'Sales', data: series }]} type="line" height={120} />
      </div>
    </div>
  );
};

const CapitalCard = () => (
  <div className="card mb-3 h-100">
    <div className="card-body">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-uppercase small text-muted">Capital</div>
        <a href="#" className="small">Hide</a>
      </div>
      <p className="mb-3">You may be eligible for a loan up to UGX 550,000,000 depending on your business health and needs. <em>Conditions apply</em>.</p>
      <div className="d-flex align-items-center">
        <div className="mr-4 text-center">
          <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}><i className="fa fa-clipboard"/></div>
          <div className="small mt-1">Start</div>
        </div>
        <div className="mr-4 text-center">
          <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}><i className="fa fa-upload"/></div>
          <div className="small mt-1">Submit</div>
        </div>
        <div className="text-center">
          <div className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center" style={{ width: 40, height: 40 }}><i className="fa fa-check"/></div>
          <div className="small mt-1">Decision</div>
        </div>
      </div>
    </div>
  </div>
);

const RecentLedgerCard = ({ items }) => {
  const [expandedList, setExpandedList] = useState({});
  const [showAll, setShowAll] = useState(false);
  const max = 4;
  const visible = showAll ? items : items.slice(0, max);

  const look = (ref) => {
    const key = (ref || '').split('-')[0];
    switch (key) {
      case 'INV': return { icon: 'fa-file-invoice-dollar', fg: '#007bff', bg: 'rgba(0,123,255,0.12)' };
      case 'PAY': return { icon: 'fa-money-bill-wave', fg: '#6c757d', bg: 'rgba(108,117,125,0.12)' };
      case 'BILL': return { icon: 'fa-file-invoice', fg: '#fd7e14', bg: 'rgba(253,126,20,0.15)' };
      case 'DEP': return { icon: 'fa-university', fg: '#17a2b8', bg: 'rgba(23,162,184,0.15)' };
      case 'JRN': return { icon: 'fa-book', fg: '#6f42c1', bg: 'rgba(111,66,193,0.12)' };
      default: return { icon: 'fa-receipt', fg: '#28a745', bg: 'rgba(40,167,69,0.12)' };
    }
  };

  const groupByDate = itemsArr => itemsArr.reduce((acc, j) => { (acc[j.date] = acc[j.date] || []).push(j); return acc; }, {});
  const groups = groupByDate(visible);
  const dates = Object.keys(groups).sort((a,b)=> b.localeCompare(a));

  const totals = (lines) => lines.reduce((t,l)=> ({ dr: t.dr + (l.dr||0), cr: t.cr + (l.cr||0) }), {dr:0, cr:0});

  return (
    <div className="card mb-3 h-100">
      <div className="card-body">
        <CardHeader title="Recent Ledger Activity" right={<span>Last 7 days</span>} />
        {dates.map(date => (
          <div key={date} className="mb-3">
            <div className="text-muted small mb-2">{date}</div>
            <ul className="list-unstyled mb-0">
              {groups[date].map(entry => {
                const { icon, fg, bg } = look(entry.ref);
                const sum = totals(entry.lines);
                const isBalanced = sum.dr === sum.cr;
                const twoLine = entry.lines.length === 2;
                const debit = twoLine ? entry.lines.find(l=>l.dr>0) : null;
                const credit = twoLine ? entry.lines.find(l=>l.cr>0) : null;
                const amount = twoLine ? (debit?.dr || credit?.cr || 0) : sum.dr;
                const open = !!expandedList[entry.id];

                return (
                  <li key={entry.id} className="py-2">
                    {/* Summary Row */}
                    <div className="d-flex align-items-center">
                      <div className="rounded-circle d-inline-flex align-items-center justify-content-center" style={{ width: 36, height: 36, background: bg, color: fg }}>
                        <i className={`fa ${icon}`} />
                      </div>
                      <div className="ml-3 flex-grow-1">
                        <div className="d-flex align-items-baseline justify-content-between">
                          <div className="mr-2">
                            <div className="font-weight-medium" style={{maxWidth: 280, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>
                              {twoLine ? (
                                <>
                                  <span className="badge badge-light border mr-1">{debit?.accountCode}</span>
                                  {debit?.accountName}
                                  <i className="fa fa-arrow-right mx-2 text-muted"/>
                                  <span className="badge badge-light border mr-1">{credit?.accountCode}</span>
                                  {credit?.accountName}
                                </>
                              ) : (
                                <>
                                  Compound entry · {entry.lines.length} lines
                                </>
                              )}
                            </div>
                            <div className="text-muted small" style={{maxWidth: 360, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{entry.memo}</div>
                            <div className="mt-1">
                              <span className="badge badge-light border mr-2 text-monospace">{entry.ref}</span>
                              <span className={`badge ${isBalanced ? 'badge-success' : 'badge-danger'}`}>{isBalanced ? 'Balanced' : 'Out of balance'}</span>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="h6 mb-0">{toUGX(amount)}</div>
                            {!twoLine && (
                              <button className="btn btn-link btn-sm p-0" onClick={()=> setExpandedList(s => ({...s, [entry.id]: !open}))}>
                                {open ? 'Hide lines' : 'Show lines'}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Detail Lines for compound entries */}
                    {!twoLine && open && (
                      <div className="mt-2 ml-5">
                        <div className="table-responsive">
                          <table className="table table-sm mb-0">
                            <thead className="thead-light">
                              <tr>
                                <th style={{width: 90}}>Code</th>
                                <th>Account</th>
                                <th className="text-right">Debit</th>
                                <th className="text-right">Credit</th>
                              </tr>
                            </thead>
                            <tbody>
                              {entry.lines.map((l, idx) => (
                                <tr key={idx}>
                                  <td className="text-monospace">{l.accountCode}</td>
                                  <td>{l.accountName}</td>
                                  <td className="text-right">{l.dr ? toUGX(l.dr) : ''}</td>
                                  <td className="text-right text-danger">{l.cr ? toUGX(l.cr) : ''}</td>
                                </tr>
                              ))}
                              <tr className="font-weight-bold">
                                <td></td>
                                <td>Total</td>
                                <td className="text-right">{toUGX(sum.dr)}</td>
                                <td className="text-right">{toUGX(sum.cr)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    <hr className="my-2"/>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {items.length > max && (
          <button className="btn btn-link btn-sm px-0" onClick={() => setShowAll((s) => !s)}>
            {showAll ? 'Show less' : `Show ${items.length - max} more`}
          </button>
        )}
        <a href="#" className="small d-inline-block ml-3">View general ledger</a>
      </div>
    </div>
  );
};

// ===================== Page =====================
export default function FinanceDashboard() {
  const [period, setPeriod] = useState('last_30_days');

  const assetsTotal = useMemo(() => ASSETS.reduce((s,a)=> s + a.balance, 0), []);
  const liabsTotal  = useMemo(() => LIABS.reduce((s,l)=> s + l.balance, 0), []);

  const bankAccounts = useMemo(() => [
    { id: 'ba1', name: '1000 Cash on hand', balance: 922050000, reviewed: true },
    { id: 'ba2', name: '1010 Checking Account', balance: 153300000 },
    { id: 'ba3', name: '2001 Credit Card', balance: -500000 },
  ], []);

  return (
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Finance"
          description="Snapshot of your company financial position"
          showsCompany
          iconClass="fa fa-chart-line"
        />

        {/* Toolbar */}
        <div className="d-flex justify-content-end mb-3">
          <div className="form-inline">
            <label className="mr-2 mb-0 small text-muted">Period</label>
            <select className="form-control form-control-sm" value={period} onChange={(e) => setPeriod(e.target.value)}>
              <option value="last_30_days">Last 30 days</option>
              <option value="last_90_days">Last 90 days</option>
              <option value="ytd">Year to date</option>
            </select>
          </div>
        </div>

        {/* Row 1: P&L focus + Financial Position */}
        <div className="row">
          <div className="col-md-4"><ProfitAndLossCard income={INCOME_LAST_30} expense={EXPENSE_LAST_30} /></div>
          <div className="col-md-4"><ExpensesDonutCard data={EXPENSES} /></div>
          <div className="col-md-4"><FinancialPositionCard assetsTotal={assetsTotal} liabsTotal={liabsTotal} /></div>
        </div>

        {/* Row 2: Operational tiles */}
        <div className="row">
          <div className="col-md-4"><BankAccountsCard accounts={bankAccounts} /></div>
          <div className="col-md-4"><InvoicesCard invoices={INVOICES} /></div>
          <div className="col-md-4">
            <div className="row">
              <div className="col-12"><SalesCard series={salesTrend} /></div>
              <div className="col-12"><CapitalCard /></div>
            </div>
          </div>
        </div>

        {/* Row 3: Recent ledger activity */}
        <div className="row">
          <div className="col-md-4"><RecentLedgerCard items={LEDGER_JOURNALS} /></div>
        </div>
      </MainContent>
    </PageContainer>
  );
}
